import styled from "styled-components";

import { AdNicheColors } from "./niches";

export type ExampleKeepoStyle = "links" | "site";

export type ExampleKeepoContent = {
  name: string;
  bio: string;
  buttons: string[];
  aboutTitle: string;
  about: string;
  headline: string;
  section: string;
  items: { name: string; detail: string }[];
  quote: string;
  quoteBy: string;
};

// "Ana Lima" → "AL"; "Dr. Paulo Mendes" → "PM"; lowercase words ("da") are skipped
const initials = (name: string) =>
  name
    .split(" ")
    .filter(word => /^\p{Lu}/u.test(word) && !word.endsWith("."))
    .slice(0, 2)
    .map(word => word[0])
    .join("");

/**
 * An invented Keepo of the niche, drawn on the phone of the ad pages (no real person, no photos):
 * "links" is a link-in-bio page, "site" a fuller page with services and a review.
 */
const ExampleKeepo = ({
  content,
  colors,
  look,
}: {
  content: ExampleKeepoContent;
  colors: AdNicheColors;
  look: ExampleKeepoStyle;
}) => {
  const [main, ...others] = content.buttons;
  return (
    <Screen $colors={colors} data-look={look}>
      {look === "links" ? (
        <Links>
          <span className="avatar">{initials(content.name)}</span>
          <b className="name">{content.name}</b>
          <span className="bio">{content.bio}</span>
          <span className="button main">{main}</span>
          {others.map(button => (
            <span key={button} className="button">
              {button}
            </span>
          ))}
          <span className="tiles">
            <i />
            <i />
            <i />
          </span>
          <span className="about">
            <b>{content.aboutTitle}</b>
            {content.about}
          </span>
        </Links>
      ) : (
        <Site>
          <span className="hero">
            <b className="name">{content.name}</b>
            <span className="headline">{content.headline}</span>
          </span>
          <span className="body">
            <span className="pill">{main}</span>
            <b className="section">{content.section}</b>
            {content.items.map(item => (
              <span key={item.name} className="item">
                <b>{item.name}</b>
                <span>{item.detail}</span>
              </span>
            ))}
            <span className="quote">
              “{content.quote}”<small>{content.quoteBy}</small>
            </span>
          </span>
        </Site>
      )}
    </Screen>
  );
};

export default ExampleKeepo;

// Drawn at the computer's phone size (298 × 570) and scaled down on the smaller phone
const Screen = styled.div<{ $colors: AdNicheColors }>`
  --bg: ${p => p.$colors.background};
  --tx: ${p => p.$colors.text};
  /* soft tones only: the form's button stays the one strong color on the page */
  --ac: color-mix(in srgb, ${p => p.$colors.accent ?? p.$colors.button} 30%, var(--bg));
  --act: var(--tx);
  --card: color-mix(in srgb, var(--tx) 7%, var(--bg));
  width: 298px;
  height: 570px;
  overflow: hidden;
  background: var(--bg);
  color: var(--tx);
  font-family: "Inter", sans-serif;
  text-align: left;
  transform: scale(0.624);
  transform-origin: top left;

  span,
  b,
  small {
    display: block;
  }

  @media (min-width: 1024px) {
    transform: none;
  }
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 22px;
  text-align: center;

  .avatar {
    display: grid;
    place-items: center;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: var(--ac);
    color: var(--act);
    font:
      600 1.875rem "Poppins",
      sans-serif;
  }

  .name {
    margin-top: 14px;
    font:
      700 1.25rem/1.2 "Poppins",
      sans-serif;
  }

  .bio {
    margin-top: 4px;
    font-size: 0.8125rem;
    opacity: 0.8;
  }

  .button {
    width: 100%;
    margin-top: 10px;
    padding: 13px;
    border-radius: 999px;
    background: var(--card);
    font:
      600 0.875rem "Poppins",
      sans-serif;
  }

  .button.main {
    margin-top: 22px;
    background: var(--ac);
    color: var(--act);
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    width: 100%;
    margin-top: 18px;
  }

  .tiles i {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
    border-radius: 12px;
    background: var(--card);
  }

  .tiles i::after {
    position: absolute;
    inset: 30% -20% -20% 30%;
    border-radius: 50%;
    content: "";
    background: var(--ac);
    opacity: 0.35;
  }

  .tiles i:nth-child(2)::after {
    inset: -20% 30% 40% -20%;
    border-radius: 30%;
  }

  .tiles i:nth-child(3)::after {
    inset: 40% 10% -30% 10%;
  }

  .about {
    width: 100%;
    margin-top: 14px;
    padding: 14px;
    border-radius: 16px;
    background: var(--card);
    font-size: 0.78rem;
    line-height: 1.45;
    text-align: left;
  }

  .about b {
    margin-bottom: 4px;
    font:
      700 0.8125rem "Poppins",
      sans-serif;
  }
`;

const Site = styled.div`
  .hero {
    position: relative;
    height: 230px;
    overflow: hidden;
    padding: 22px;
    background: var(--ac);
    color: var(--act);
  }

  /* two soft shapes in the corner, under the words */
  .hero::before,
  .hero::after {
    position: absolute;
    content: "";
    background: var(--bg);
  }

  .hero::before {
    right: -70px;
    bottom: -90px;
    width: 190px;
    height: 190px;
    border-radius: 50%;
    opacity: 0.25;
  }

  .hero::after {
    top: 18px;
    right: 22px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    opacity: 0.35;
  }

  .name {
    position: relative;
    font:
      700 0.9375rem "Poppins",
      sans-serif;
  }

  .headline {
    position: relative;
    max-width: 220px;
    margin-top: 46px;
    font:
      700 1.6875rem/1.08 "Poppins",
      sans-serif;
    letter-spacing: -0.01em;
  }

  .body {
    padding: 18px 20px;
  }

  .pill {
    display: inline-block;
    padding: 11px 18px;
    border-radius: 999px;
    background: var(--ac);
    color: var(--act);
    font:
      600 0.8125rem "Poppins",
      sans-serif;
  }

  .section {
    margin: 20px 0 10px;
    font:
      700 0.9375rem "Poppins",
      sans-serif;
  }

  .item {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--card);
    font-size: 0.8125rem;
  }

  .item b {
    font-weight: 600;
  }

  .item span {
    opacity: 0.7;
  }

  .quote {
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--card);
    font-size: 0.8125rem;
    line-height: 1.4;
  }

  .quote small {
    margin-top: 4px;
    opacity: 0.7;
  }
`;
